import type { CurrentWorkspace, ExcludedProjectFile, WorkspaceOperation } from '@mio/shared';

const reasons: Record<ExcludedProjectFile['reason'], string> = {
  excluded_path: 'private, generated or dependency path',
  unsupported_file: 'unsupported file type',
  symbolic_link: 'symbolic link',
  special_file: 'not a regular file',
  oversized_file: 'larger than the 256 KiB limit',
};

function Exclusions({ files }: { files: ExcludedProjectFile[] | null | undefined }) {
  if (!files)
    return <p>File coverage was not recorded for this older save. Save again to check it.</p>;
  // This marker is created by Mio itself, never learner work.
  const visible = files.filter((file) => file.path !== '.mio-activity.json');
  if (!visible.length) return <p>No learner files were excluded from this save.</p>;
  return (
    <details>
      <summary className="cursor-pointer">
        {visible.length} {visible.length === 1 ? 'path was' : 'paths were'} not backed up or
        included for review
      </summary>
      <ul className="mt-2 max-h-40 list-disc overflow-auto pl-5">
        {visible.map((file) => (
          <li key={file.path}>
            <code className="break-all">{file.path}</code> — {reasons[file.reason]}
          </li>
        ))}
      </ul>
      <p className="mt-2">
        These files remain on the workspace disk; a backup cannot restore them.
      </p>
    </details>
  );
}

export function WorkshopCheckpoint({
  checkpoint,
  operation,
  attemptId,
}: {
  checkpoint: CurrentWorkspace['checkpoint'];
  operation: WorkspaceOperation | null;
  attemptId: string | null;
}) {
  const outgoing =
    operation?.capture && operation.capture.attemptId !== attemptId ? operation.capture : null;
  return (
    <section className="text-fg-muted mb-4 space-y-2 text-xs" aria-label="Saved file coverage">
      <p>
        Backups and reviews include supported source and text files only. Credentials, dependencies,
        symbolic links and unsupported files such as images and notebooks are excluded.
      </p>
      {checkpoint ? (
        <div>
          <p>Last saved checkpoint: {new Date(checkpoint.capturedAt).toLocaleString()}.</p>
          <Exclusions files={checkpoint.excluded} />
        </div>
      ) : null}
      {outgoing ? (
        <div>
          <p>Previous activity saved before switching:</p>
          <Exclusions files={outgoing.excluded} />
        </div>
      ) : null}
    </section>
  );
}
